/**
 * Public /one-pagers page — browse + download persona 1-pager PDFs.
 *
 * Used by the founding team to share branded marketing collateral
 * with prospects: a single, one-page PDF per audience that explains
 * exactly what Armstrong K9 Registry does for THEM, why to join, and
 * one CTA URL. Each card lets the visitor preview, download, or copy
 * a deep link.
 *
 * All six PDFs are generated server-side from `services.persona_one_pager`
 * so the same canonical copy that lives in the Python module renders
 * here AND in the PDF.
 */
import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { FileArrowDownIcon as FileArrowDown, LinkSimpleIcon as LinkSimple, ArrowRightIcon as ArrowRight, TreeStructureIcon as TreeStructure, MagnifyingGlassIcon as MagnifyingGlass, ScissorsIcon as Scissors, TruckIcon as Truck, StorefrontIcon as Storefront, DogIcon as Dog, CertificateIcon as Certificate, AirplaneIcon as Airplane, PawPrintIcon as PawPrint, HouseIcon as House, HeartIcon as Heart, GraduationCapIcon as GraduationCap } from "@phosphor-icons/react";

const ICONS = {
  breeder: TreeStructure,
  buyer: MagnifyingGlass,
  service_provider: Scissors,
  transporter: Truck,
  storefront_seller: Storefront,
  dog_owner: Dog,
  registration: Certificate,
  // International Lead Marketplace personas (added 2026-08-15)
  intl_transporter: Airplane,
  intl_walker: PawPrint,
  intl_sitter: House,
  intl_groomer: Heart,
  intl_trainer: GraduationCap,
};

export default function OnePagers() {
  const { user, loading } = useAuth();
  const [items, setItems] = useState(null);

  useEffect(() => {
    api
      .get("/public/one-pagers")
      .then((r) => setItems(r.data?.one_pagers || []))
      .catch(() => setItems([]));
  }, []);

  const copy = async (relUrl) => {
    const absolute = `${API.replace(/\/api$/, "")}${relUrl}`;
    try {
      await navigator.clipboard.writeText(absolute);
      toast.success("Deep link copied");
    } catch {
      toast.error("Could not copy");
    }
  };

  return (
    <AppShell>
      {!loading && (!user || user.role !== "admin") && <Navigate to="/" replace />}
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-12 md:py-20">
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-crimson mb-3" data-testid="one-pagers-eyebrow">
          Sales &amp; outreach collateral
        </p>
        <h1 className="font-spectral font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
          One-page flyers for every audience.
        </h1>
        <p className="mt-5 text-[15px] text-[#3F3F3F] max-w-2xl">
          Twelve branded PDFs — one per persona (six U.S.-facing + five international Lead Marketplace pros + a focused $25-registration explainer) — that explain Armstrong K9 Registry in a single page. Use them in cold emails, Instagram DMs, printed flyers, or partner intros. The link to each PDF is permanent &amp; cache-friendly.
        </p>

        {!items ? (
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5 animate-pulse">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="rounded-2xl border border-[#EFEFEF] bg-white h-64" />
            ))}
          </div>
        ) : (
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="one-pagers-grid">
            {items.map((p) => {
              const Icon = ICONS[p.key] || Dog;
              return (
                <div
                  key={p.key}
                  className="rounded-2xl border border-[#EFEFEF] bg-white p-6 flex flex-col transition hover:border-ink hover:shadow-[0_12px_28px_-16px_rgba(0,0,0,0.22)]"
                  data-testid={`one-pager-card-${p.key}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-[#FBF7EC] text-gold-dark">
                      <Icon size={17} weight="duotone" />
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.22em] text-[#8A6A00] font-bold">{p.eyebrow}</span>
                  </div>
                  <h3 className="font-spectral text-2xl text-ink leading-tight mb-2">{p.headline}</h3>
                  <p className="text-[13px] text-[#3F3F3F] leading-snug flex-1 mb-5">{p.subheading}</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <a
                      href={`${API.replace(/\/api$/, "")}${p.pdf_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-full bg-ink hover:bg-crimson text-white text-[12.5px] font-semibold px-4 h-9 transition-colors"
                      data-testid={`one-pager-download-${p.key}`}
                    >
                      <FileArrowDown size={13} weight="duotone" /> Open PDF
                    </a>
                    <button
                      type="button"
                      onClick={() => copy(p.pdf_url)}
                      className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E5E5] hover:bg-[#FBF7EC] hover:border-gold text-ink text-[12.5px] font-semibold px-4 h-9 transition-colors"
                      data-testid={`one-pager-copy-${p.key}`}
                    >
                      <LinkSimple size={13} weight="duotone" /> Copy link
                    </button>
                    <Link
                      to={p.cta_path}
                      className="ml-auto text-[12px] font-semibold text-ink hover:text-crimson underline underline-offset-4"
                      data-testid={`one-pager-cta-${p.key}`}
                    >
                      {p.cta_label}
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-16 rounded-2xl border border-[#E8D9A6] bg-[#FBF7EC] p-6 md:p-8">
          <h3 className="font-spectral text-2xl text-ink mb-2">How to use these</h3>
          <ul className="text-[14px] text-[#3F3F3F] leading-relaxed space-y-1.5">
            <li><strong>Cold email a kennel?</strong> Send the breeder PDF.</li>
            <li><strong>Posting to a dog-owner Facebook group?</strong> Drop the buyer or dog-owner PDF.</li>
            <li><strong>Pitching a local trainer?</strong> Service-provider PDF — now includes live video sessions + courses.</li>
            <li><strong>Recruiting transporters at a USDA-licensed convention?</strong> Transporter PDF.</li>
            <li><strong>Inviting a dog-food brand to open a Storefront?</strong> Storefront-seller PDF.</li>
            <li><strong>Reaching international couriers, walkers, sitters, groomers, or trainers?</strong> Use the five intl-persona PDFs — they explain the Lead Marketplace Hybrid pricing model up front so applicants know exactly what they're buying.</li>
            <li><strong>Running a $25-registration ad campaign?</strong> Use the dedicated registration PDF as the email attachment + landing-page handout.</li>
          </ul>
          <p className="mt-4 text-[12px] text-[#6B6B6B]">
            Every link is permanent — bookmark and reuse. PDFs are cached at the CDN edge for 1 hour, so updates ship in under an hour.
          </p>
        </div>
      </div>
    </AppShell>
  );
}
